/**
 * WelcomeDepletionDialog — first-purchase conversion prompt.
 * Shown when a never-purchased user can't afford a generation.
 * Features achievement stats, card-stack hero visual, and single Starter pack CTA.
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountStore } from '@/store/accountStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { capabilities } from '@/capabilities.ts';
import { requestCreditPurchase, setPendingPackDetails } from '@/bridge/embeddedBridge.ts';
import { TOKEN_PACKS } from '@/ai/tokenCosts.ts';
import { createCheckoutSession } from '@/api/stripeApi.ts';
import { trackEvent } from '@/telemetry/trackEvent';
import styles from './WelcomeDepletionDialog.module.css';

const STARTER_PACK = TOKEN_PACKS[0]; // pack_100, 100 tokens, $5

function formatPrice(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

function buildAchievementLine(
  t: (key: string, opts?: Record<string, unknown>) => string,
  history: { assetType: string }[],
): string | null {
  const images = history.filter(h => h.assetType === 'image').length;
  const videos = history.filter(h => h.assetType === 'video' || h.assetType === 'transition_video').length;
  const audio = history.filter(h => h.assetType === 'audio' || h.assetType === 'tts').length;

  const cats: { count: number; label: string }[] = [];
  if (images > 0) cats.push({ count: images, label: images === 1 ? t('depletionCategoryImage') : t('depletionCategoryImages') });
  if (videos > 0) cats.push({ count: videos, label: videos === 1 ? t('depletionCategoryVideo') : t('depletionCategoryVideos') });
  if (audio > 0) cats.push({ count: audio, label: audio === 1 ? t('depletionCategoryAudioSingular') : t('depletionCategoryAudio') });

  if (cats.length === 0) return null;

  if (cats.length === 1) {
    return t('depletionAchievement1', { count0: cats[0].count, label0: cats[0].label });
  }
  if (cats.length === 2) {
    return t('depletionAchievement2', {
      count0: cats[0].count, label0: cats[0].label,
      count1: cats[1].count, label1: cats[1].label,
    });
  }
  return t('depletionAchievement3', {
    count0: cats[0].count, label0: cats[0].label,
    count1: cats[1].count, label1: cats[1].label,
    count2: cats[2].count, label2: cats[2].label,
  });
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function WelcomeDepletionDialog({ open, onClose }: Props) {
  const { t } = useTranslation('account');
  const accountBalance = useAccountStore(s => s.accountBalance);
  const setTokenPurchaseDialogOpen = useAccountStore(s => s.setTokenPurchaseDialogOpen);
  const generationHistory = useAiStore(s => s.generationHistory);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const price = formatPrice(STARTER_PACK.priceCents);
  const isEmbedded = capabilities.bridge;

  // Fire depletion_shown once when this dialog mounts
  useEffect(() => {
    trackEvent('depletion_shown', { context: 'ai_panel' });
  }, []);

  // Build achievement line from project generation history
  const achievementLine = buildAchievementLine(t, generationHistory);

  // Find the last visual generation (image or video) for the hero card — skip audio/TTS
  const lastVisual = [...generationHistory]
    .reverse()
    .find(h => h.assetType === 'image' || h.assetType === 'video' || h.assetType === 'transition_video') ?? null;
  const isVideo = lastVisual?.mimeType?.startsWith('video/') ?? false;

  const handlePurchase = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      if (isEmbedded) {
        const oldBalance = accountBalance?.available ?? 0;
        setPendingPackDetails({ pack_id: STARTER_PACK.id, credits: STARTER_PACK.tokens, price_cents: STARTER_PACK.priceCents });
        await requestCreditPurchase(STARTER_PACK.id);
        useAccountStore.getState().showIapSuccess({
          packLabel: STARTER_PACK.label,
          creditsAdded: STARTER_PACK.tokens,
          oldBalance,
          newBalance: oldBalance + STARTER_PACK.tokens,
        });
        // Mark as purchased so this dialog won't show again
        useAccountStore.setState({ hasEverPurchased: true });
        onClose();
      } else {
        const checkoutUrl = await createCheckoutSession(STARTER_PACK.id);
        window.open(checkoutUrl, '_blank');
        useAccountStore.setState({ hasEverPurchased: true });
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorPurchaseFailed'));
    }
    setLoading(false);
  }, [isEmbedded, accountBalance, onClose, t]);

  const handleViewPacks = useCallback(() => {
    onClose();
    setTokenPurchaseDialogOpen(true);
  }, [onClose, setTokenPurchaseDialogOpen]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>&times;</button>

        {/* Card stack */}
        <div className={styles.cardStack}>
          <div className={styles.card1} />
          <div className={styles.card2} />
          <div className={styles.card3} />
          <div className={styles.heroCard}>
            {lastVisual ? (
              isVideo ? (
                <video
                  className={styles.heroMedia}
                  src={lastVisual.objectUrl}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              ) : (
                <img
                  className={styles.heroMedia}
                  src={lastVisual.objectUrl}
                  alt=""
                />
              )
            ) : (
              <div className={styles.heroFallback}>✨</div>
            )}
          </div>
        </div>

        {/* Copy + CTA */}
        <div className={styles.content}>
          <div className={styles.achievementLine}>
            {achievementLine ?? t('depletionFallback')}
          </div>
          <div className={styles.headline}>
            {t('depletionHeadline', { credits: STARTER_PACK.tokens, price })}
          </div>

          <button
            className={styles.ctaBtn}
            onClick={handlePurchase}
            disabled={loading}
          >
            {loading
              ? (isEmbedded ? t('processingButton') : t('openingButton'))
              : t('depletionCta', { price })}
          </button>

          <button className={styles.viewPacksLink} onClick={handleViewPacks}>
            {t('depletionViewPacks')}
          </button>

          {error && <div className={styles.error}>{error}</div>}
        </div>
      </div>
    </div>
  );
}
